import type { KeyboardEvent } from "react";
import { cn } from "@/lib/utils";
import type { GoldenDimension } from "@/services/adminModelCurrency";

interface ScoreControlProps {
  dimension: GoldenDimension;
  value: number | undefined;
  onChange: (score: number) => void;
}

/** A 1-5 picker for one scoring dimension. Pressing a number key while it is focused picks that score. */
const ScoreControl = ({ dimension, value, onChange }: ScoreControlProps) => {
  const { min, max } = dimension.scale;
  const scores = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= min && n <= max) onChange(n);
  }

  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span id={`dim-${dimension.key}`} className="text-sm font-medium">
          {dimension.label}
        </span>
        <span className="text-xs text-muted-foreground">
          {min} = poor, {max} = excellent
        </span>
      </div>
      <div
        role="radiogroup"
        aria-labelledby={`dim-${dimension.key}`}
        className="flex gap-1.5"
        onKeyDown={handleKeyDown}
      >
        {scores.map(score => (
          <button
            key={score}
            type="button"
            role="radio"
            aria-checked={value === score}
            aria-label={String(score)}
            onClick={() => onChange(score)}
            className={cn(
              "h-9 w-9 rounded-md border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-ring",
              value === score
                ? "bg-primary text-primary-foreground"
                : "bg-background hover:bg-accent"
            )}
          >
            {score}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ScoreControl;
